import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import './Homepage.css';

const PUBLIC = process.env.PUBLIC_URL || '';
// Network images may be blocked (CSP / adblock / network restrictions).
// Use an inline SVG placeholder so tiles never render blank.
const FALLBACK_IMG = (() => {
  const svg = `
  <svg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600">
    <defs>
      <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="#007acc" stop-opacity="0.25"/>
        <stop offset="1" stop-color="#ff4757" stop-opacity="0.15"/>
      </linearGradient>
    </defs>
    <rect width="800" height="600" fill="url(#g)"/>
    <rect x="48" y="52" width="704" height="496" rx="28" fill="rgba(255,255,255,0.55)" />
    <text x="400" y="310" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="28" font-weight="700" fill="#1a1a2e">
      USL Sports
    </text>
    <text x="400" y="355" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="16" font-weight="600" fill="#7f8c8d">
      Image placeholder (network blocked)
    </text>
  </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg.trim())}`;
})();

const HomeImage = ({ src, className, alt = '' }) => (
  <img
    src={src}
    alt={alt}
    className={className}
    onError={(e) => {
      // If network images fail (CSP/adblock/blocked), keep layout filled.
      e.currentTarget.onerror = null;
      e.currentTarget.src = FALLBACK_IMG;
    }}
  />
);

const IconTruck = () => (
  <svg className="home-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
    <path d="M1 3h15v13H1V3z" strokeLinejoin="round" />
    <path d="M16 8h4l3 3v5h-7V8z" strokeLinejoin="round" />
    <circle cx="5.5" cy="18.5" r="1.5" />
    <circle cx="18.5" cy="18.5" r="1.5" />
  </svg>
);

const IconCard = () => (
  <svg className="home-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
  </svg>
);

const IconRefresh = () => (
  <svg className="home-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
    <path d="M3 12a9 9 0 0115-6.7L21 8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M21 12a9 9 0 01-15 6.7L3 16" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconHeadset = () => (
  <svg className="home-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden>
    <path d="M4 14v3a2 2 0 002 2h1M20 14v3a2 2 0 01-2 2h-1" strokeLinecap="round" />
    <path d="M4 14a8 8 0 0116 0" />
  </svg>
);

const ESSENTIALS = [
  {
    title: 'Cricket gear',
    blurb: 'Bats, balls, gloves, pads, and protection — curated for UK play.',
    img: `${PUBLIC}/images/cricket gear .jpeg`,
    action: 'products',
  },
  {
    title: 'Team apparel',
    blurb: 'Jerseys and kits with pro-grade fabrics and consistent colour.',
    img: `${PUBLIC}/images/apeel.jfif`,
    action: 'products',
  },
  {
    title: '3D kit designer',
    blurb: 'Build your look with live preview, then order with confidence.',
    img: 'https://loremflickr.com/800/600/sports,team,kit?lock=103',
    action: 'kit-designer',
  },
];

const FEATURED = [
  {
    name: 'Custom team kit',
    price: 'From £49.99',
    img: `${PUBLIC}/images/jerseys/custom.jfif`,
    badge: 'Popular',
  },
  {
    name: 'Performance jersey',
    price: 'See catalog',
    img: `${PUBLIC}/images/performance.jfif`,
    badge: null,
  },
  {
    name: 'Training range',
    price: 'Shop deals',
    img: `${PUBLIC}/images/training.jfif`,
    badge: 'New',
  },
  {
    name: 'Club bundles',
    price: 'Bulk quotes',
    img: `https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=600&q=80`,
    badge: null,
  },
];

const TESTIMONIALS = [
  {
    name: 'Alex J., London',
    title: 'Exceptional quality',
    text:
      'The kits arrived on time and the print quality exceeded what we expected for our league side.',
  },
  {
    name: 'Sophie L., Manchester',
    title: 'One place for gear',
    text:
      'From tape-ball practice gear to match kits, Universal Sports Lab has been reliable for our squad.',
  },
  {
    name: 'Mark P., Birmingham',
    title: 'Great value',
    text:
      'Competitive pricing without cutting corners — we keep coming back for new seasons.',
  },
  {
    name: 'Emma T., Leeds',
    title: 'Coach approved',
    text:
      'Easy to recommend to juniors and seniors alike; the team helps you pick the right spec.',
  },
];

const PROMO_BLOCKS = [
  {
    kicker: 'Up to 30% off',
    title: 'Tape ball cricket',
    cta: 'Shop tape ball',
    action: 'products',
  },
  {
    kicker: 'Match ready',
    title: 'Hard ball gear',
    cta: 'Shop hard ball',
    action: 'products',
  },
  {
    kicker: 'Design online',
    title: 'Custom 3D kits',
    cta: 'Open designer',
    action: 'kit-designer',
  },
];

const HERO_SLIDES = [
  {
    eyebrow: 'Universal Sports Lab',
    title: 'CRICKET: WHERE LEGENDS ARE MADE',
    lead:
      'From breathtaking sixes to match-winning centuries, gear up with premium bats, gloves, balls, and apparel trusted for UK conditions.',
    ctaLabel: 'Shop now',
    ctaAction: 'products',
    img: `${PUBLIC}/images/slider1.jpg`,
  },
  {
    eyebrow: 'Universal Sports Lab',
    title: 'Unleash your inner cricketer',
    lead:
      'Premium cricket and team kit for peak performance — plus a 3D kit designer for custom looks.',
    ctaLabel: 'Shop now',
    ctaAction: 'products',
    img: `${PUBLIC}/images/slider2.jpg`,
  },
  {
    eyebrow: 'Universal Sports Lab',
    title: 'Custom 3D kit designer',
    lead: 'Build your look with live preview, then order with confidence.',
    ctaLabel: 'Open designer',
    ctaAction: 'kit-designer',
    img: `${PUBLIC}/images/slider3.jpg`,
  },
];

const Homepage = ({ onExploreProducts, onNavigate }) => {
  const [activeSlide, setActiveSlide] = useState(0);

  const slides = useMemo(() => HERO_SLIDES, []);

  useEffect(() => {
    const t = setInterval(() => {
      setActiveSlide((i) => (i + 1) % slides.length);
    }, 5500);
    return () => clearInterval(t);
  }, [slides.length]);

  const goToSlide = (idx) => {
    setActiveSlide(((idx % slides.length) + slides.length) % slides.length);
  };

  const go = (id) => {
    if (typeof onNavigate === 'function') onNavigate(id);
  };

  const runEssential = (action) => {
    if (action === 'kit-designer') go('kit-designer');
    else onExploreProducts();
  };

  return (
    <div className="homepage homepage--swl">
      {/* Top bar — retail-style trust line */}
      <div className="home-topbar" role="region" aria-label="Promotions">
        <div className="home-container home-topbar-inner">
          <span>Free UK delivery on qualifying orders</span>
          <span className="home-topbar-dot" aria-hidden />
          <span>Secure checkout</span>
          <span className="home-topbar-dot" aria-hidden />
          <span>Built for clubs &amp; teams</span>
        </div>
      </div>

      {/* Primary hero — full-bleed banner */}
      <section className="home-hero home-hero--primary" aria-labelledby="home-hero-1-heading">
        <div className="home-hero-bg" aria-hidden>
          <div
            className="home-hero-bg-image"
            style={{ backgroundImage: `url(${slides[activeSlide]?.img})` }}
          />
        </div>
        <div className="home-container home-hero-grid">
          <motion.div
            key={`copy-${activeSlide}`}
            className="home-hero-copy"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <p className="home-hero-eyebrow">{slides[activeSlide]?.eyebrow}</p>
            <h1 id="home-hero-1-heading" className="home-hero-title">
              {slides[activeSlide]?.title}
            </h1>
            <p className="home-hero-lead">{slides[activeSlide]?.lead}</p>
          </motion.div>
        </div>

        {/* Slider controls */}
        <div className="home-hero-carousel-controls" aria-label="Hero carousel controls">
          <button
            type="button"
            className="home-hero-carousel-arrow"
            onClick={() => goToSlide(activeSlide - 1)}
            aria-label="Previous slide"
          >
            ‹
          </button>
          <div className="home-hero-carousel-dots" role="tablist" aria-label="Select slide">
            {slides.map((s, idx) => (
              <button
                key={s.title}
                type="button"
                className={`home-hero-carousel-dot ${idx === activeSlide ? 'is-active' : ''}`}
                onClick={() => goToSlide(idx)}
                aria-label={`Slide ${idx + 1}`}
              />
            ))}
          </div>
          <button
            type="button"
            className="home-hero-carousel-arrow"
            onClick={() => goToSlide(activeSlide + 1)}
            aria-label="Next slide"
          >
            ›
          </button>
        </div>
      </section>

      {/* Secondary hero — light band */}
      <section className="home-hero home-hero--secondary" aria-labelledby="home-hero-2-heading">
        <div className="home-container home-hero-secondary-inner">
          <div className="home-hero-secondary-copy">
            <h2 id="home-hero-2-heading" className="home-section-h2">
              Unleash your inner cricketer
            </h2>
            <p className="home-hero-secondary-text">
              Gear up with premium bats, gloves, balls, and apparel. Universal Sports Lab brings you elite cricket and team kit for performance on every pitch — plus a 3D designer when you want a custom look.
            </p>
            <div className="home-hero-secondary-actions">
              <button type="button" className="home-btn home-btn--dark" onClick={onExploreProducts}>
                Shop now
              </button>
              <button type="button" className="home-btn home-btn--outline" onClick={() => go('kit-designer')}>
                Custom kit designer
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip — Klarna / delivery style */}
      <div className="home-trust-strip">
        <div className="home-container home-trust-grid">
          <div className="home-trust-item">
            <IconCard />
            <span>Flexible payment options</span>
          </div>
          <div className="home-trust-item">
            <IconTruck />
            <span>Free UK delivery on qualifying orders</span>
          </div>
          <div className="home-trust-item">
            <IconRefresh />
            <span>Hassle-free returns window</span>
          </div>
        </div>
      </div>

      {/* Essentials — category tiles */}
      <section className="home-section" aria-labelledby="essentials-heading">
        <div className="home-container">
          <p className="home-section-kicker">Shop by category</p>
          <h2 id="essentials-heading" className="home-section-h2 home-section-h2--center">
            Cricket essentials
          </h2>
          <p className="home-section-sub home-section-sub--center">
            Explore gloves, bats, balls, apparel, and custom design — everything you need to perform.
          </p>
          <div className="home-essentials-grid">
            {ESSENTIALS.map((item, i) => (
              <motion.article
                key={item.title}
                className="home-essential-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
              >
                <div className="home-essential-media">
                  <HomeImage src={item.img} alt="" className="home-essential-img" />
                </div>
                <h3 className="home-essential-title">{item.title}</h3>
                <p className="home-essential-blurb">{item.blurb}</p>
                <button
                  type="button"
                  className="home-link-cta"
                  onClick={() => runEssential(item.action)}
                >
                  {item.action === 'kit-designer' ? 'Open designer' : 'Shop now'}
                </button>
              </motion.article>
            ))}
          </div>

          <div className="home-quick-links" aria-label="Quick links">
            <button type="button" className="home-chip" onClick={onExploreProducts}>
              Tape ball
            </button>
            <button type="button" className="home-chip" onClick={onExploreProducts}>
              Hard ball
            </button>
            <button type="button" className="home-chip" onClick={() => go('kit-designer')}>
              Kit designer
            </button>
            <button type="button" className="home-chip" onClick={() => go('about')}>
              About USL
            </button>
            <button type="button" className="home-chip" onClick={() => go('contact')}>
              Contact
            </button>
          </div>
        </div>
      </section>

      {/* Featured products — grid */}
      <section className="home-section home-section--muted" aria-labelledby="featured-heading">
        <div className="home-container">
          <h2 id="featured-heading" className="home-section-h2 home-section-h2--center">
            Featured picks
          </h2>
          <p className="home-section-sub home-section-sub--center">
            Popular routes teams take — tap through to the full catalog.
          </p>
          <div className="home-product-grid">
            {FEATURED.map((p, i) => (
              <motion.article
                key={p.name}
                className="home-product-card"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
              >
                <div className="home-product-media">
                  {p.badge ? <span className="home-product-badge">{p.badge}</span> : null}
                  <HomeImage src={p.img} alt="" className="home-product-img" />
                </div>
                <div className="home-product-body">
                  <h3 className="home-product-name">{p.name}</h3>
                  <button type="button" className="home-btn home-btn--small home-btn--dark" onClick={onExploreProducts}>
                    View in shop
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="home-section" aria-labelledby="reviews-heading">
        <div className="home-container">
          <h2 id="reviews-heading" className="home-section-h2 home-section-h2--center">
            Our happy customers
          </h2>
          <div className="home-reviews-grid">
            {TESTIMONIALS.map((t, i) => (
              <motion.blockquote
                key={t.name}
                className="home-review-card"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
              >
                <p className="home-review-title">{t.title}</p>
                <p className="home-review-text">&ldquo;{t.text}&rdquo;</p>
                <footer className="home-review-author">{t.name}</footer>
              </motion.blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* Promo banners — three columns */}
      <section className="home-section home-section--promo" aria-label="Highlights">
        <div className="home-container home-promo-grid">
          {PROMO_BLOCKS.map((b, i) => (
            <motion.div
              key={b.title}
              className="home-promo-card"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
            >
              <p className="home-promo-kicker">{b.kicker}</p>
              <h3 className="home-promo-title">{b.title}</h3>
              <button
                type="button"
                className="home-btn home-btn--inverse"
                onClick={() => (b.action === 'kit-designer' ? go('kit-designer') : onExploreProducts())}
              >
                {b.cta}
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Value props — footer-style row */}
      <section className="home-value-row" aria-label="Service benefits">
        <div className="home-container home-value-grid">
          <div className="home-value-item">
            <IconTruck />
            <h3 className="home-value-title">Delivery</h3>
            <p className="home-value-text">Straightforward shipping on qualifying orders.</p>
          </div>
          <div className="home-value-item">
            <IconCard />
            <h3 className="home-value-title">Flexible payment</h3>
            <p className="home-value-text">Pay the way that suits your club or squad.</p>
          </div>
          <div className="home-value-item">
            <IconRefresh />
            <h3 className="home-value-title">Returns</h3>
            <p className="home-value-text">Clear policy so you can shop with confidence.</p>
          </div>
          <div className="home-value-item">
            <IconHeadset />
            <h3 className="home-value-title">Support</h3>
            <p className="home-value-text">Reach out via Contact for orders and questions.</p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="home-final-cta" aria-labelledby="final-cta-heading">
        <div className="home-container home-final-inner">
          <h2 id="final-cta-heading" className="home-final-heading">
            Ready to kit up?
          </h2>
          <p className="home-final-sub">Browse the catalog or open the 3D designer — same checkout flow you already use.</p>
          <div className="home-final-actions">
            <button type="button" className="home-btn home-btn--light" onClick={onExploreProducts}>
              Browse products
            </button>
            <button type="button" className="home-btn home-btn--outline-light" onClick={() => go('kit-designer')}>
              Kit designer
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Homepage;
